import { Controller } from "@hotwired/stimulus";

/**
 * Currency Controller - Currency selection & conversion (replaces jQuery CurrencyManage)
 *
 * Responsibilities:
 * • Exchange rate fetching & caching
 * • Currency selection & persistence (cookies)
 * • Real-time price conversion and formatting
 * • Currency symbol updates
 * • Estimate warning display
 * • Inter-controller communication via currency:changed events
 *
 * Feature was built for the new pricing pages, so it currently only operates inside the `#plans` element
 * (where `data-controller="currency"` is set). The dropdown can be rendered in multiple places
 * inside that element e.g. above the pricing cards and above the addon section, and
 * all instances stay in sync: picking a currency in one updates the button label of the other.
 * Each render needs unique element ids (`suffix:` arg in `pricing_page_modern_helper.rb`),
 * but stimulus targets stay the same so the controller treats them as one control.
 *
 * NOTE:
 * To use this elsewhere in the app, move `data-controller="currency"`
 * up to a parent that contains every place the dropdown should appear. Keep the existing
 * pricing-page renders inside that scope, and don't put it on `<body>` unless every page
 * needs it (the controller fetches exchange rates on mount).
 *
 * If a use-case comes up where you want the dropdown around but there are no prices to
 * convert, the controller will still happily run, but at that point it would be worth
 * gating the API fetch on whether any price targets exist, to avoid the wasted call.
 *
 */

export default class extends Controller {
  static API_URL = "https://api.exchangerate.host/latest?base=EUR";
  static STORAGE_KEY = "webinargeek_sel_curr";

  static targets = [
    "currencySymbol",
    "priceAmount",
    "dropdownButton",
    "dropdownItem",
    "dropdownContainer",
    "warning",
    "estimate"
  ];

  static values = {
    definitions: Object,
    selectedCurrency: { type: String, default: "eur" },
    rates: Object,
    accountCurrency: String,
    countryCurrency: String,
    baseCurrency: String
  };

  connect() {
    // Listen for pricing updates from pricing controller (must be before early return)
    // Store bound function so addEventListener and removeEventListener use the same reference (.bind() creates a new function each time)
    this.pricesUpdatedHandler = this.handlePricesUpdated.bind(this);
    this.element.addEventListener("pricing:prices-updated", this.pricesUpdatedHandler);

    // If dealing with native payments (non-EUR account), set to account currency without conversion
    if (this.isNativePaymentAccount()) {
      const accountCurrency = this.normalizeCurrency(this.accountCurrencyValue);
      if (accountCurrency && this.definitionsValue[accountCurrency]) {
        this.selectedCurrencyValue = accountCurrency;
        this.ratesValue = {}; // No conversion needed for native payments
        this.updateDisplay();
      }
      this.hideDropdown();
    } else {
      // For EUR accounts or multi-currency scenarios, fetch rates and show selector
      this.fetchRates()
        .then(() => {
          this.initializeSelectedCurrency();
          this.updateDisplay();
          this.showDropdown();
        })
        .catch(error => {
          console.error("Failed to fetch currency rates:", error);
          // Still show selector with fallback rates
          this.initializeSelectedCurrency();
          this.updateDisplay();
          this.showDropdown();
        });
    }
  }

  showDropdown() {
    this.dropdownContainerTargets.forEach(el => el.classList.remove("hidden"));
  }

  hideDropdown() {
    this.dropdownContainerTargets.forEach(el => el.classList.add("hidden"));
  }

  disconnect() {
    this.element.removeEventListener("pricing:prices-updated", this.pricesUpdatedHandler);
  }

  handlePricesUpdated() {
    // Re-convert prices after pricing controller updates data-base-amount attributes.
    // Use queueMicrotask to ensure all DOM mutations are fully settled before reading values.
    queueMicrotask(() => {
      this.updateCurrencySymbols();
      this.updatePriceAmounts();
    });
  }

  // Helper Methods

  isNativePaymentAccount() {
    return this.accountCurrencyValue && this.baseCurrencyValue && this.accountCurrencyValue !== this.baseCurrencyValue;
  }

  normalizeCurrency(code) {
    return code?.toLowerCase();
  }

  get selectedCurrency() {
    return this.definitionsValue[this.selectedCurrencyValue];
  }

  async fetchRates() {
    try {
      const response = await fetch(this.constructor.API_URL);
      if (!response.ok) throw new Error("API request failed");

      const data = await response.json();
      this.ratesValue = data.rates || {};
    } catch (error) {
      console.warn("Using fallback rates due to API error:", error);
      this.ratesValue = {};
    }
  }

  initializeSelectedCurrency() {
    const storedCurrency = this.getStoredCurrency();
    const availableAbbrs = this.availableAbbreviations();

    if (storedCurrency && this.definitionsValue[storedCurrency]) {
      this.selectedCurrencyValue = storedCurrency;
    } else if (this.countryCurrencyValue && availableAbbrs.includes(this.countryCurrencyValue.toUpperCase())) {
      this.selectedCurrencyValue = this.countryCurrencyValue.toLowerCase();
    } else {
      this.selectedCurrencyValue = "eur";
    }
  }

  selectCurrency(event) {
    event.preventDefault();

    const currencyCode = this.normalizeCurrency(event.currentTarget.dataset.currency);
    if (!currencyCode || !this.definitionsValue[currencyCode]) return;

    this.storeCurrency(currencyCode);
    this.selectedCurrencyValue = currencyCode;
  }

  selectedCurrencyValueChanged() {
    this.updateDisplay();
    this.dispatchCurrencyChanged();
  }

  updateDisplay() {
    this.updateCurrencySymbols();
    this.updatePriceAmounts();
    this.updateDropdown();
    this.updateWarning();
  }

  updateCurrencySymbols() {
    const symbol = this.selectedCurrency.symbol;
    this.currencySymbolTargets.forEach(el => {
      el.textContent = symbol;
    });
  }

  updatePriceAmounts() {
    const rate = this.getSelectedRate();

    this.priceAmountTargets.forEach(el => {
      const base = parseFloat(el.dataset.baseAmount);
      if (Number.isNaN(base)) return;

      const decimals = parseInt(el.dataset.decimals || 0);
      const converted = base * rate;

      el.textContent = this.formatConvertedAmount(converted, decimals);
    });
  }

  formatConvertedAmount(amount, decimals) {
    if (decimals > 0) {
      const amountWithDecimals = amount.toFixed(decimals);
      const formatted = this.formatAmount(amountWithDecimals);
      // Strip trailing .00 if present
      return formatted.replace(/[.,]00$/, "");
    }

    return Math.round(amount);
  }

  updateDropdown() {
    if (!this.hasDropdownButtonTarget) return;

    const currencyCode = this.selectedCurrencyValue;

    const selectedItem = this.dropdownItemTargets.find(item => item.dataset.currency === currencyCode);
    if (selectedItem) {
      const buttonHTML = selectedItem.innerHTML + " <span class=\"caret\"></span>";
      this.dropdownButtonTargets.forEach(btn => btn.innerHTML = buttonHTML);
    }

    this.dropdownItemTargets.forEach(item => {
      item.classList.toggle("active", item.dataset.currency === currencyCode);
    });
  }

  updateWarning() {
    const isEstimate = !this.isNativePaymentAccount() && this.selectedCurrencyValue !== "eur";

    this.warningTargets.forEach(el => el.classList.toggle("hidden", !isEstimate));
    this.estimateTargets.forEach(el => el.style.display = isEstimate ? "" : "none");
  }

  dispatchCurrencyChanged() {
    this.dispatch("changed", {
      detail: {
        currency: this.selectedCurrencyValue,
        symbol: this.selectedCurrency.symbol,
        rate: this.getSelectedRate()
      },
      bubbles: true
    });
  }

  getSelectedRate() {
    // For native payment accounts, prices are already in account currency - no conversion needed
    if (this.isNativePaymentAccount()) return 1;

    if (this.selectedCurrencyValue === "eur") return 1;

    const abbr = this.selectedCurrency.abbr;

    if (!this.ratesValue || !this.ratesValue[abbr]) {
      console.warn("Using fallback rate for", abbr);
      return this.selectedCurrency.fallbackRate;
    }

    return this.ratesValue[abbr];
  }

  formatAmount(amount) {
    const separator = this.selectedCurrency.decimalSeparator || ".";
    return amount.toString().replace(".", separator);
  }

  availableAbbreviations() {
    return Object.values(this.definitionsValue).map(c => c.abbr);
  }

  getStoredCurrency() {
    return localStorage.getItem(this.constructor.STORAGE_KEY);
  }

  storeCurrency(value) {
    localStorage.setItem(this.constructor.STORAGE_KEY, value);
  }
}
